import { mapState } from 'vuex';
import ApiService from '@/services/apiService';
import uniq from 'lodash/uniq';
export const getUniqueValues = (arr, key) => {
  return arr.reduce((acc, curr) => {
    if (!acc.includes(curr[key])) {
      acc.push(curr[key]);
    }
    return acc;
  }, []);
};
export const groupByKey = (data, key) => {
  return data.reduce((acc, curr) => {
    const k = String(curr[key]) || 'undefined';
    if (!acc[k]) {
      acc[k] = [];
    }
    acc[k].push(curr.value);
    return acc;
  }, {});
};
const chartColors = ['#004e73', '#005e8a', '#006ea1', '#007eb8', '#445fa0', '#5272c0', '#6085e0', '#6e98ff', '#9865ba', '#b779df', '#d58dff', '#f4a2ff', '#eb64b4', '#ff78d8', '#ff8cfc', '#ffa0ff', '#ff6b92', '#ff81af', '#ff96cc', '#ffacea', '#ff935c', '#ffb16f', '#ffce81', '#ffec94', '#ffcf00', '#fff900', '#ffff00', '#ffff00'];
export default {
  name: 'CoreVirtualDeviceDialogStatistics',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    searchTypes: {
      type: Array,
      default: () => []
    },
    searchItems: {
      type: Array,
      default: () => []
    },
    selectedAccounts: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      chartConfig: {
        legend: {
          display: false
        },
        scales: {
          responsive: false,
          maintainAspectRatio: false,
          yAxes: [{
            ticks: {
              suggestedMin: 0
            }
          }],
          xAxes: [{
            type: 'time',
            distribution: 'series',
            time: {
              unit: 'day',
              unitStepSize: 1,
              stepSize: 1,
              minUnit: 'day',
              tooltipFormat: 'LL'
            },
            // time: {
            //   displayFormats: {
            //     minute: this.language === 'de' ? 'HH:mm' : 'hh:mm A',
            //     hour: this.language === 'de' ? 'HH:mm' : 'hh:mm A',
            //   },
            // },
            stacked: true
          }]
        }
      },
      rolledOutPerConnector: {},
      rolledOutPerAccount: {},
      dateStart: null,
      dateStop: null,
      accounts: [],
      multiSearch: [],
      // eslint-disable-next-line no-empty-function
      accountsPromise: new Promise(() => {})
    };
  },
  computed: {
    ...mapState('app', ['color', 'UiTheme', 'language']),
    accountsLoaded() {
      return this.accounts.length > 0;
    },
    hasRolledOutPerConnectorResults() {
      return this.rolledOutPerConnector.individual && this.rolledOutPerConnector.individual.length;
    },
    hasRolledOutPerAccountResults() {
      return this.rolledOutPerAccount.individual && this.rolledOutPerAccount.individual.length;
    }
  },
  watch: {
    open: {
      handler(newOpen) {
        if (newOpen) {
          this.resetToDefaults();
        }
      },
      immediate: true
    }
  },
  methods: {
    resetToDefaults() {
      this.rolledOutPerConnector = {};
      this.rolledOutPerAccount = {};
      this.dateStart = null;
      this.dateStop = null;
      this.multiSearch = this.searchItems;

      // if accounts are already fetched then set accountsPromise to resolved
      // so fetchByConnector() and fetchByAccount() will be called
      if (this.accounts.length === 0) {
        this.accountsPromise = Promise.resolve();
        this.fetchAccountPlain();
      }
      this.fetchByConnector();
      this.fetchByAccount();
    },
    findAccountById(id) {
      return this.accounts.find(a => a.id === parseInt(id, 10));
    },
    fetchAccountPlain() {
      this.$store.dispatch('app/loadAccounts').then(accounts => {
        this.accounts = accounts.map(item => {
          return {
            id: item.id,
            name: item.name
          };
        });
      }).catch(error => {
        console.error(error);
      });
    },
    createFilter(data, param, key = null) {
      const filter = data.reduce((acc, item) => {
        return item.param !== param ? acc : {
          ...acc,
          values: [...acc.values, String(item.search.value || item.search)]
        };
      }, {
        by: key ? key : param,
        values: []
      });
      return filter.values.length > 0 ? [filter] : [];
    },
    createFilterAccounts(accounts, key = 'account_id') {
      return accounts.length > 0 ? [{
        by: key,
        values: accounts.map(account => String(account.id))
      }] : [];
    },
    getOptions(groupBy, aggregation = '1d') {
      return {
        ...(this.dateStart && {
          start: String(this.dateStart)
        }),
        ...(this.dateStop && {
          stop: String(this.dateStop)
        }),
        filters: [...this.createFilterAccounts(this.selectedAccounts, 'account_id'), ...this.createFilter(this.multiSearch, 'title', 'twin_title'), ...this.createFilter(this.multiSearch, 'group', 'groups'), ...this.createFilter(this.multiSearch, 'tag', 'tags'), ...this.createFilter(this.multiSearch, 'bridge.type', 'connector_type'), ...this.createFilter(this.multiSearch, 'deviceType', 'device_type_id'), ...this.createFilter(this.multiSearch, 'id', 'twin_id'), ...this.createFilter(this.multiSearch, 'operationalStatus', 'operational_status'), ...this.createFilter(this.multiSearch, 'health', 'health')],
        groupBy: groupBy,
        aggregationWindow: aggregation
      };
    },
    fetchByConnector() {
      this.accountsPromise.then(() => {
        return this.fetchStatistics(this.getOptions('connector_type')).then(response => {
          this.rolledOutPerConnector = response;
        });
      }).catch(error => {
        this.rolledOutPerConnector.error = error;
        console.error(error);
      });
    },
    fetchByAccount() {
      this.accountsPromise.then(() => {
        return this.fetchStatistics(this.getOptions('account_id')).then(response => {
          response.individual = response.individual.map(item => ({
            ...item,
            label: (this.findAccountById(item.label) || {}).name || item.label
          }));
          this.rolledOutPerAccount = response;
        });
      }).catch(error => {
        this.rolledOutPerAccount.error = error;
        console.error(error);
      });
    },
    fetchStatistics(options = {}) {
      return ApiService.virtualDeviceStatistics(null, options).then(response => {
        return {
          common: {
            labels: uniq(response.map(entry => entry.time))
          },
          individual: Object.entries(groupByKey(response, 'groupby')).map((entry, index) => {
            return {
              label: entry[0],
              color: chartColors[index],
              disableHoverColor: true,
              data: entry[1]
            };
          })
        };
      });
    },
    searchUpdated(search) {
      this.multiSearch = search;
      this.fetchByConnector();
      this.fetchByAccount();
    },
    handleDateChange(result) {
      this.dateStart = new Date(result.from * 1000).toISOString();
      this.dateStop = new Date(result.to * 1000).toISOString();
      this.fetchByConnector();
      this.fetchByAccount();
    }
  }
};